import {
  OrderBrief,
  OrderDetails,
  OrderAction,
  ORDER_STATUS,
  ORDER_STATUS_NAME_MAP,
} from '../../../data/types/orders';
import ShowImage from '../../../components/show_image';
import { AttributeAssociation } from '../../../data/types/listings';
import { TRANSACTION_METHODS_NAME_MAP } from '../../../data/types/transactions';
import { formateServerDateToLocaleDate } from '../../../utils/functions/time_date_functions';
import moment from 'moment';
import { shiftTypeFormatStartAndEndDateTime } from '../../../data/types/shifts';

export const DisplayItemAttribute = ({
  attribute,
}: {
  attribute: AttributeAssociation;
}) => {
  if (attribute.attribute_type === 1) {
    return (
      <button className="numeric-attr-card">{attribute.attr_value_en}</button>
    );
  }
  if (attribute.attribute_type === 2 && !attribute.attr_value_image) {
    return (
      <button className="numeric-attr-card">{attribute.attr_value_en}</button>
    );
  }
  if (attribute.attribute_type === 2 && attribute.attr_value_image) {
    return (
      <span className="select-attr-card">
        <ShowImage
          image={attribute.attr_value_image}
          style={{
            width: '40px',
            height: '45px',
            borderRadius: '5px',
          }}
        />
      </span>
    );
  }
  if (attribute.attribute_type === 3) {
    return (
      <button className="boolean-attr-card">
        {attribute.attr_value_en ? 'True' : 'False'}
      </button>
    );
  }

  // Add a default return statement here
  return <div>Unknown attribute type</div>;
};

export const getCustomersOptionsFromOrders = (orders: OrderBrief[]) => {
  const getCustomersFromOrders = (orders: OrderBrief[]) => {
    if (!orders) return [];
    return (
      orders
        .map((item) => [
          item.customer_id,
          item.destination_address.display_name,
        ])
        // get unique elements only
        .filter(
          (value, index, self) =>
            self.findIndex((item) => item[0] === value[0]) === index,
        )
    );
  };
  if (!orders) return [];
  return getCustomersFromOrders(orders).map((item) => ({
    label: `#${item[0]} ` + item[1],
    value: item[0],
  }));
};

export const getSellersOptionsFromOrders = (orders: OrderBrief[]) => {
  const getSellersFromOrders = (orders: OrderBrief[]) => {
    if (!orders) return [];
    return (
      orders
        .map((item) => [item.store_id, item.source_address.display_name])
        // get unique elements only
        .filter(
          (value, index, self) =>
            self.findIndex((item) => item[0] === value[0]) === index,
        )
        .filter((value, index, self) => self.indexOf(value) === index)
    );
  };
  if (!orders) return [];
  return getSellersFromOrders(orders).map((item) => ({
    label: `#${item[0]} ` + item[1],
    value: item[0],
  }));
};

export const getShiftsOptionsFromOrders = (orders: OrderBrief[]) => {
  const getShiftsFromOrders = (orders: OrderBrief[]) => {
    if (!orders) return [];
    return (
      orders
        .map((item) => [
          item.last_mile_shift_id,
          shiftTypeFormatStartAndEndDateTime(item.last_mile_shift),
        ])
        // get unique elements only
        .filter(
          (value, index, self) =>
            self.findIndex((item) => item[0] === value[0]) === index,
        )
        .filter((value, index, self) => self.indexOf(value) === index)
    );
  };
  if (!orders) return [];
  return getShiftsFromOrders(orders).map((item) => ({
    label: `#${item[0]} ` + item[1],
    value: item[0],
  }));
};

export const getPaymentOptionsFromOrders = (orders: OrderBrief[]) => {
  const getPaymentFromOrders = (orders: OrderBrief[]) => {
    if (!orders) return [];
    return orders
      .map((item) => item.payment_type)
      .filter((value, index, self) => self.indexOf(value) === index); // get unique elements only
  };
  if (!orders) return [];
  return getPaymentFromOrders(orders).map((item) => ({
    label: TRANSACTION_METHODS_NAME_MAP[item],
    value: item,
  }));
};

export const getStatusByName = (name: string) => {
  const status = ORDER_STATUS[name as keyof typeof ORDER_STATUS]; // Type assertion for clarity
  if (status !== undefined) {
    return {
      type_id: status.toString().toUpperCase(), // Convert status value to string for type_id
      read_able_name:
        ORDER_STATUS_NAME_MAP[status as keyof typeof ORDER_STATUS_NAME_MAP], // Use the provided name for readability
    };
  }
  return { type_id: '0', read_able_name: name };
};

export const formatOrderDateTime = (
  order: OrderBrief | OrderDetails,
  status?: number,
) => {
  if (!status) return <>{formateServerDateToLocaleDate(order.placing_date)}</>;
  const statusDate = order.order_actions.find(
    (statusItem) => statusItem.action === status,
  );
  return <>{formateServerDateToLocaleDate(statusDate?.action_date)}</>;
};

export const getLatestStatus = (actions: OrderAction[]) => {
  actions = actions.sort((b, a) => {
    return b.action - a.action;
  });
  if (actions) {
    const lastStatus = actions[actions.length - 1];
    return lastStatus;
  }
  return null;
};

export function requiresSupportAttention(order: OrderBrief) {
  return (
    (order.status === ORDER_STATUS.requested ||
      order.status === ORDER_STATUS.accepted) &&
    moment(
      order.shipments?.[order.shipments.length - 1]?.last_mile_shift
        .start_datetime,
    ) < moment().add(6, 'hours')
  );
}
